import { useTranslation } from "react-i18next";
import type { SchemaElement } from "../../../../api/restaurantSchema";
import { ELEMENT_STYLES, OBJECT_KINDS } from "../../../../components/restaurant-schema/elementCatalog";
import { PersonIcon } from "../../../../components/restaurant-schema/schemaIcons";

// Номерата естествено: 2 преди 10.
const byTableName = (left: SchemaElement, right: SchemaElement) =>
  (left.name ?? "").localeCompare(right.name ?? "", undefined, { numeric: true });

/**
 * Списъкът по групи (дизайн, т. 8): масите отделно (клик = избор, синхрон със
 * схемата), под тях обектите, групирани по вид с брой („Растение ×2").
 */
const ElementList = ({
  elements,
  selectedId,
  onSelect,
}: {
  elements: SchemaElement[];
  selectedId: number | null;
  onSelect: (elementId: number) => void;
}) => {
  const { t } = useTranslation();
  const tables = elements.filter((element) => element.kind === "table").sort(byTableName);
  const seatCount = tables.reduce((sum, table) => sum + (table.seats ?? 0), 0);
  const objectGroups = OBJECT_KINDS.map((kind) => ({
    kind,
    items: elements.filter((element) => element.kind === kind),
  })).filter((group) => group.items.length > 0);
  const objectCount = elements.length - tables.length;

  return (
    <section className="flex min-h-[240px] flex-1 flex-col overflow-hidden rounded-[14px] border border-shade-2 bg-surface lg:min-h-0">
      <div className="flex items-baseline justify-between px-4 pb-2 pt-3.5">
        <span className="text-[14px] font-bold text-ink">{t("restaurantSchema.list.tables")}</span>
        <span className="text-[12.5px] tabular-nums text-muted">
          {tables.length} · {t("restaurantSchema.counts.seats", { count: seatCount })}
        </span>
      </div>
      <div className="flex min-h-0 flex-1 flex-col gap-0.5 overflow-auto px-2 pb-2.5">
        {tables.length === 0 && (
          <div className="px-2 py-2 text-[12.5px] text-muted">{t("restaurantSchema.list.empty")}</div>
        )}
        {tables.map((table) => {
          const selected = table.id === selectedId;
          return (
            <button
              key={table.id}
              type="button"
              onClick={() => onSelect(table.id)}
              className={`flex h-10 flex-none cursor-pointer items-center gap-2.5 rounded-[8px] border-none px-2 text-left transition-colors duration-150 ${
                selected ? "bg-brand-tint" : "bg-transparent hover:bg-panel"
              }`}
            >
              <span
                className={`flex h-[26px] min-w-[26px] shrink-0 items-center justify-center border-[1.5px] border-schema-table-edge px-0.5 text-[12px] font-bold text-ink ${
                  table.tableShape === "round" ? "rounded-full" : "rounded-[7px]"
                }`}
              >
                {table.name}
              </span>
              <span className="min-w-0 flex-1 truncate text-[13.5px] font-semibold leading-[1.3] text-ink">
                {t("restaurantSchema.table.title", { name: table.name })}
              </span>
              <span className="flex items-center gap-1 text-[12.5px] tabular-nums text-body">
                <PersonIcon className="h-3 w-3" strokeWidth={2.4} />
                {table.seats}
              </span>
            </button>
          );
        })}

        {objectGroups.length > 0 && (
          <>
            <div className="mt-2 flex items-baseline justify-between border-t border-shade-2 px-2 pb-1.5 pt-3.5">
              <span className="text-[10.5px] font-bold uppercase tracking-[1px] text-muted">
                {t("restaurantSchema.list.objects")}
              </span>
              <span className="text-[12px] tabular-nums text-muted">{objectCount}</span>
            </div>
            {objectGroups.map(({ kind, items }) => {
              const style = ELEMENT_STYLES[kind];
              const isSelected = items.some((item) => item.id === selectedId);
              return (
                <button
                  key={kind}
                  type="button"
                  // Клик избира първия от вида; всеки следващ клик — следващия.
                  onClick={() => {
                    const index = items.findIndex((item) => item.id === selectedId);
                    onSelect(items[(index + 1) % items.length].id);
                  }}
                  className={`flex h-[34px] flex-none cursor-pointer items-center gap-2.5 rounded-[8px] border-none px-2 text-left text-[13px] text-ink-2 transition-colors duration-150 ${
                    isSelected ? "bg-brand-tint" : "bg-transparent hover:bg-panel"
                  }`}
                >
                  <span
                    className={`flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-[6px] ${style.chipClass}`}
                  >
                    <style.Icon className="h-3 w-3" strokeWidth={2.4} />
                  </span>
                  <span className="flex-1">{t(style.labelKey)}</span>
                  {items.length > 1 && (
                    <span className="text-[12px] tabular-nums text-muted">×{items.length}</span>
                  )}
                </button>
              );
            })}
          </>
        )}
      </div>
    </section>
  );
};

export default ElementList;
